/**
 * Juror panel: scored, sortable, filterable list + detail modal.
 * Faithful to the monolith's renderPanel/loadDemoJurors/openJurorDetail.
 */

import type { Juror } from '@/types';
import { store, panelContext } from '@/app/store';
import { scoreJuror, getScoreLabel, getFlags } from '@/core/scoring';
import { DEMO_JURORS } from '@/data/demo-jurors';
import { byId } from '@/ui/dom';
import { showToast, confirmApp, showModal, closeModal } from '@/ui/overlays';
import { svAssessmentHtml } from '@/features/juror-sv';
import { vdCaptureHtml } from '@/features/vd-capture';
import { renderPanelState } from '@/features/panel-state';
import { escapeHTML } from '@/utils/formatters';
import { JURY_FORM_TEMPLATE } from '@/ocr/jury-form-template';
import { applyRecoveredField } from '@/ocr/field-apply';
import {
  specificQuestions,
  assessSuitability,
  fillTemplate,
  type ChargeInfo,
  type AngleInput
} from '@/core/vd-specific';
import type { CaselawDB, JuryResearchHit } from '@/data/caselaw-db';

type PanelSort = 'number' | 'score';
let panelSort: PanelSort = 'number';
let panelFilter = 'all';

const STATUS_COLORS: Record<string, string> = {
  none: '',
  keep: '#16a34a',
  strike: '#dc2626',
  seated: '#2563eb',
  challenged: '#d97706'
};
const STATUS_LABELS: Record<string, string> = {
  none: '',
  keep: 'Keep',
  strike: 'Strike',
  seated: 'Seated',
  challenged: 'Challenged'
};

export function setPanelSort(mode: PanelSort): void {
  panelSort = mode;
  renderPanel();
}

export function setPanelFilter(filter: string): void {
  panelFilter = filter;
  renderPanel();
}

export function updatePanelBadge(): void {
  const badge = byId('panel-badge');
  if (!badge) return;
  const count = store.getState().jurors.length;
  badge.style.display = count > 0 ? 'block' : 'none';
  badge.textContent = String(count);
}

/** Normalise a flag side to the CSS suffix used by .flag-def / .flag-pros. */
function flagClass(side: string): 'def' | 'pros' {
  return side === 'defense' || side === 'def' ? 'def' : 'pros';
}

export function renderPanel(): void {
  const list = byId('panel-list');
  if (!list) return;
  const state = store.getState();
  const ctx = panelContext();
  const side = state.panelLens;

  const sortBar = byId('panel-sort-bar');
  if (sortBar) {
    const btn = (mode: PanelSort, label: string): string =>
      `<button class="btn btn-sm ${panelSort === mode ? 'btn-primary' : 'btn-secondary'}" ` +
      `onclick="setPanelSort('${mode}')" style="padding:3px 8px;font-size:10px;">${label}</button>`;
    sortBar.innerHTML =
      '<span style="font-size:10px;color:var(--text-muted);margin-right:5px;">Sort:</span>' +
      btn('number', 'Juror #') +
      btn('score', 'Score');
  }

  let jurors = state.jurors.slice().sort((a, b) => {
    if (panelSort === 'number') {
      const na = parseInt((a.panelId || '').replace(/\D/g, ''), 10) || 9999;
      const nb = parseInt((b.panelId || '').replace(/\D/g, ''), 10) || 9999;
      return na - nb;
    }
    return side === 'defense'
      ? scoreJuror(b, ctx) - scoreJuror(a, ctx)
      : scoreJuror(a, ctx) - scoreJuror(b, ctx);
  });
  if (panelFilter !== 'all') jurors = jurors.filter((j) => j.status === panelFilter);

  const total = state.jurors.length;
  const seated = state.jurors.filter((j) => j.status === 'seated').length;
  const struck = state.jurors.filter((j) => j.status === 'strike' || j.status === 'challenged').length;
  const summary = byId('panel-summary');
  if (summary) summary.textContent = `${total} jurors · ${seated} seated · ${struck} struck`;
  renderPanelState();

  if (!jurors.length) {
    list.innerHTML =
      '<div class="empty-state"><div class="empty-icon">👥</div>' +
      `<div class="empty-title">${panelFilter === 'all' ? 'No jurors yet' : 'No jurors in this category'}</div>` +
      `<div class="empty-sub">${panelFilter === 'all' ? 'Use the Scan tab to add jurors' : ''}</div></div>`;
    return;
  }

  list.innerHTML = jurors
    .map((j) => {
      const sc = scoreJuror(j, ctx);
      const sl = getScoreLabel(sc, side);
      const flags = getFlags(j, ctx);
      const statusColor = STATUS_COLORS[j.status] || '';
      const statusLabel = STATUS_LABELS[j.status] || '';
      const barPct = Math.abs(sc) * 10;
      const barColor = sc > 0 ? 'var(--green)' : sc < 0 ? 'var(--red)' : 'var(--text-muted)';
      const meta = [j.occ || j.bizType || '—', j.edu, j.hhSize ? `HH ${j.hhSize}` : '']
        .filter(Boolean)
        .join(' · ');
      return (
        `<div class="juror-card" onclick="openJurorDetail(${j.id})" style="border-left:3px solid ${statusColor || 'transparent'};">` +
        `<div class="anon-id">${escapeHTML(j.panelId)}</div>` +
        '<div class="juror-info">' +
        '<div style="display:flex;align-items:center;gap:5px;">' +
        `<div class="juror-name">Seat ${j.seat} · ${escapeHTML(j.gender || '—')}, ${escapeHTML(j.age || '—')}</div>` +
        (statusLabel
          ? `<span style="font-size:9px;font-weight:700;padding:2px 6px;border-radius:8px;background:${statusColor}22;color:${statusColor};">${statusLabel}</span>`
          : '') +
        '</div>' +
        `<div class="juror-meta">${escapeHTML(meta)}</div>` +
        `<div class="score-bar-wrap"><div class="score-bar-fill" style="width:${barPct}%;background:${barColor};"></div></div>` +
        (flags.length
          ? '<div class="juror-flags">' +
            flags
              .map((f) => `<span class="flag-chip flag-${flagClass(f.side)}">${escapeHTML(f.label)}</span>`)
              .join('') +
            '</div>'
          : '') +
        '</div>' +
        `<div class="score-badge ${sl.cls}">${sl.label}</div>` +
        '</div>'
      );
    })
    .join('');
}

/** Format one stored questionnaire value for display (checkbox arrays → list). */
function formatQValue(v: string | number | string[] | null | undefined): string {
  if (Array.isArray(v)) return v.length ? v.map((s) => s.replace(/_/g, ' ')).join(', ') : '—';
  if (v == null || v === '') return '—';
  return String(v);
}

/**
 * Render the complete captured questionnaire (every template field, in order)
 * for the juror detail modal, headed by the upload date + juror key. Returns ''
 * for jurors with no scan (demo/manual), so only scanned jurors show this.
 */
function questionnaireHtml(j: Juror): string {
  const q = j.questionnaire;
  if (!q) return '';
  const uploaded = j.capturedAt ? new Date(j.capturedAt).toLocaleString() : null;
  const rows = JURY_FORM_TEMPLATE.fields
    .map(
      (f) =>
        `<div><div class="dl">${escapeHTML(f.label)}</div><div class="dv">${escapeHTML(formatQValue(q[f.id]))}</div></div>`
    )
    .join('');
  return (
    '<div style="margin-top:16px;padding-top:12px;border-top:1px solid var(--border,#e2e8f0);">' +
    '<div style="font-size:11px;font-weight:700;color:var(--text-muted);text-transform:uppercase;letter-spacing:.4px;margin-bottom:2px;">Full questionnaire (as captured)</div>' +
    `<div style="font-size:11px;color:var(--text-muted);margin-bottom:8px;">Juror key #${j.id}${uploaded ? ' · uploaded ' + escapeHTML(uploaded) : ''}</div>` +
    '<div style="display:grid;grid-template-columns:1fr 1fr;gap:6px;">' +
    rows +
    '</div></div>'
  );
}

/**
 * "Restricted data" section: what the scanner deliberately did NOT capture
 * (identifying fields blocked by design) and how many PII fragments were
 * redacted from this juror's free-text answers. Derived at render time — the
 * rejected content itself is never stored. Only shown for scanned jurors.
 */
function restrictedDataHtml(j: Juror): string {
  if (!j.questionnaire) return '';
  // Identifying questionnaire fields the template never reads (see privacy.ts).
  const blocked = [
    'Name',
    'Date of birth',
    'Place of birth',
    'Home address',
    'Employer name',
    'Spouse/partner name'
  ];
  // Count [redacted] markers the free-text scrubber left in stored answers.
  const redactions: string[] = [];
  for (const f of JURY_FORM_TEMPLATE.fields) {
    const v = j.questionnaire[f.id];
    if (typeof v === 'string' && v.includes('[redacted]')) {
      const n = (v.match(/\[redacted\]/g) || []).length;
      redactions.push(`${f.label} (${n})`);
    }
  }
  return (
    '<div style="margin-top:16px;padding-top:12px;border-top:1px solid var(--border,#e2e8f0);">' +
    '<div style="font-size:11px;font-weight:700;color:#b45309;text-transform:uppercase;letter-spacing:.4px;margin-bottom:6px;">Questionnaire data not stored (restricted)</div>' +
    `<div style="font-size:11px;color:var(--text-secondary);line-height:1.6;">Blocked by design — never read from the form: ${blocked.map((b) => escapeHTML(b)).join(', ')}.</div>` +
    (redactions.length
      ? `<div style="font-size:11px;color:var(--text-secondary);line-height:1.6;margin-top:4px;">PII redacted inside free-text answers: ${redactions.map((r) => escapeHTML(r)).join('; ')} — shown as “[redacted]” above.</div>`
      : '<div style="font-size:11px;color:var(--text-muted);margin-top:4px;">No PII had to be redacted from this juror’s free-text answers.</div>') +
    '</div>'
  );
}

/** Map core Juror columns to jury_field_map field ids (research guidance). */
const GUIDANCE_FIELDS: Array<{ id: string; label: string; value: (j: Juror) => string }> = [
  { id: 'age', label: 'Age', value: (j) => j.age },
  { id: 'education_highest_grade', label: 'Education', value: (j) => j.edu },
  { id: 'work_school_current_past', label: 'Employment', value: (j) => j.empStatus },
  { id: 'type_of_business', label: 'Business Type', value: (j) => j.bizType },
  { id: 'job_occupation', label: 'Occupation', value: (j) => j.occ },
  { id: 'household_status', label: 'Household', value: (j) => j.hhSize },
  { id: 'parent_guardian_of_children', label: 'Parent/Guardian', value: (j) => j.parent },
  { id: 'experience_with_law', label: 'Experience with the Law', value: (j) => [j.jury, j.le, j.victim, j.accused].filter((v) => v && v !== 'None' && v !== 'No').join('; ') },
  { id: 'law_related_employment', label: 'Law-Related Employment', value: (j) => j.lawEmp },
  { id: 'impartiality', label: 'Impartiality', value: (j) => j.fair }
];

/**
 * Resolve a jury_field_map field id to this juror's answer: the scanned
 * questionnaire value when present, else the matching core Juror column.
 */
function fieldValueFor(j: Juror, id: string): string {
  const q = j.questionnaire?.[id];
  if (Array.isArray(q)) return q.join(', ');
  if (q != null && String(q).trim()) return String(q);
  const core = GUIDANCE_FIELDS.find((g) => g.id === id);
  if (core) return (core.value(j) || '').trim();
  if (id === 'spouse_age' || id === 'partner_age') return (j.spouseAge || '').trim();
  if (id === 'spouse_partner_education') return (j.spouseEdu || '').trim();
  return '';
}

/** Spouse/partner field ids in jury_field_map (asked about the juror's household). */
const SPOUSE_FIELDS: Array<{ id: string; label: string }> = [
  { id: 'spouse_age', label: 'Spouse/Partner Age' },
  { id: 'spouse_partner_education', label: 'Spouse/Partner Education' },
  { id: 'spouse_partner_occupation', label: 'Spouse/Partner Occupation' },
  { id: 'spouse_type_of_business', label: 'Spouse/Partner Business' },
  { id: 'spouse_work_school', label: 'Spouse/Partner Work/School' }
];

/** Pull voir dire question strings out of a research entry's stored data. */
function questionsFromResearch(data: Record<string, unknown> | null): string[] {
  if (!data) return [];
  const out: string[] = [];
  for (const key of ['voir_dire', 'voir_dire_topics', 'voir_dire_questions', 'voir_dire_followups']) {
    const v = data[key];
    if (Array.isArray(v)) out.push(...v.filter((x): x is string => typeof x === 'string'));
  }
  if (typeof data.voir_dire_probe === 'string') out.push(data.voir_dire_probe);
  return out;
}

/**
 * Parse the Case tab's charge list into ChargeInfo rows, with charge-category
 * labels resolved through the charge→research map. Shared by the suitability
 * assessment and the voir dire generator.
 */
function buildChargeInfos(db: CaselawDB): Array<ChargeInfo & { hits: Array<JuryResearchHit & { label: string }> }> {
  const out: Array<ChargeInfo & { hits: Array<JuryResearchHit & { label: string }> }> = [];
  for (const c of store.getState().chargeList) {
    const m = c.text.match(/^(\d+[A-Z]?)-([A-Z0-9]+)/i);
    if (!m) continue;
    const chapter = m[1].toUpperCase();
    const section = m[2].toUpperCase();
    const name = c.text.replace(/^\S+\s*/, '').trim() || `c. ${chapter} § ${section}`;
    const hits = db.researchForCharge(chapter, section);
    out.push({ chapter, section, name, labels: [...new Set(hits.map((h) => h.label))], hits });
  }
  return out;
}

/**
 * Fill the juror detail's side-aware suitability box: how suitable this juror
 * is FOR YOUR CLIENT given the charged crime — the numeric score, the flags,
 * and the charge-mapped research angles this juror's answers actually trigger,
 * plus what to pin down in voir dire. Async; silent when the DB is unavailable.
 */
async function renderJurorSuitability(j: Juror): Promise<void> {
  const el = byId('jr-suitability');
  if (!el) return;
  try {
    const { getCaselawDb } = await import('@/data/caselaw-db-instance');
    const db = await getCaselawDb();
    if (!db) return;
    const charges = buildChargeInfos(db);
    if (!charges.length) {
      el.innerHTML =
        '<div style="background:#f8fafc;border:1px dashed #cbd5e1;border-radius:8px;padding:8px 11px;margin-bottom:12px;font-size:11px;color:var(--text-muted);">Enter the case’s charges on the Case tab to get a charge-specific suitability analysis and voir dire questions for this juror.</div>';
      return;
    }
    const side = store.getState().panelLens;
    const score = scoreJuror(j, panelContext());
    const flags = getFlags(j, panelContext());

    // Research angles for the current side that this juror's answers trigger.
    const yes = (v: string | undefined): boolean => !!v && /^yes/i.test(v.trim());
    const stated = (v: string | undefined): boolean =>
      !!v && !/^(no|none|none stated|not found|—|n\/a)$/i.test(v.trim());
    const triggers: Array<{ re: RegExp; on: boolean }> = [
      { re: /victim/i, on: yes(j.victim) },
      { re: /police|law enforcement|legitimacy/i, on: stated(j.le) },
      { re: /prior jury|previous jury/i, on: yes(j.jury) },
      { re: /publicity|pretrial/i, on: stated(j.caseKnow) || stated(j.opinion) },
      { re: /death.qualification|gruesome|emotion/i, on: true }, // charge-level, always relevant
      { re: /insanity|diminished/i, on: true }
    ];
    const angles: AngleInput[] = [];
    const seenTopic = new Set<string>();
    for (const ch of charges) {
      for (const h of ch.hits) {
        const angle = side === 'defense' ? h.defense : h.prosecution;
        if (!angle || !h.topic || seenTopic.has(h.topic)) continue;
        if (!triggers.some((t) => t.on && t.re.test(h.topic as string))) continue;
        seenTopic.add(h.topic);
        angles.push({ topic: h.topic, angle });
      }
    }

    const a = assessSuitability(j, side, score, flags, charges, angles);
    const leanColor =
      a.lean === 'favorable' ? '#166534' : a.lean === 'unfavorable' ? '#991b1b' : a.lean === 'high-risk-unknown' ? '#b45309' : '#475569';
    const leanBg =
      a.lean === 'favorable' ? '#f0fdf4' : a.lean === 'unfavorable' ? '#fef2f2' : a.lean === 'high-risk-unknown' ? '#fffbeb' : '#f8fafc';
    el.innerHTML =
      `<div style="background:${leanBg};border:1px solid ${leanColor}33;border-radius:8px;padding:10px 12px;margin-bottom:12px;">` +
      `<div style="font-size:10px;font-weight:800;color:${leanColor};text-transform:uppercase;letter-spacing:.4px;margin-bottom:4px;">Suitability for your client — ${escapeHTML(a.lean.replace(/-/g, ' '))}</div>` +
      `<div style="font-size:12px;color:var(--text-primary);line-height:1.55;font-weight:600;">${escapeHTML(a.headline)}</div>` +
      (a.points.length
        ? '<ul style="margin:6px 0 0;padding-left:16px;">' +
          a.points.map((p) => `<li style="font-size:10.5px;color:var(--text-secondary);line-height:1.5;margin-bottom:3px;">${escapeHTML(p)}</li>`).join('') +
          '</ul>'
        : '') +
      `<div style="font-size:10px;font-weight:700;color:${leanColor};margin-top:6px;">Before deciding:</div>` +
      '<ul style="margin:3px 0 0;padding-left:16px;">' +
      a.probes.map((p) => `<li style="font-size:10.5px;color:var(--text-secondary);line-height:1.5;margin-bottom:3px;">${escapeHTML(p)}</li>`).join('') +
      '</ul></div>';
  } catch {
    /* research DB unavailable — leave empty */
  }
}

/**
 * Fill the juror detail's "Suggested voir dire questions" section: questions
 * drawn from (a) the research mapped to the CHARGED crimes in the active case
 * and (b) this juror's own answers — including spouse/partner background —
 * via the questionnaire-field research map. Async; silent when unavailable.
 */
async function renderJurorVoirDire(j: Juror): Promise<void> {
  const el = byId('jr-voirdire');
  if (!el) return;
  try {
    const { getCaselawDb } = await import('@/data/caselaw-db-instance');
    const db = await getCaselawDb();
    if (!db) return;

    const seen = new Set<string>();
    const dedupe = (qs: string[]): string[] =>
      qs.filter((q) => {
        const k = q.toLowerCase().replace(/\s+/g, ' ').trim();
        if (!k || seen.has(k)) return false;
        seen.add(k);
        return true;
      });
    const qList = (qs: string[]): string =>
      '<ul style="margin:4px 0 0;padding-left:16px;">' +
      qs.map((q) => `<li style="font-size:10.5px;color:var(--text-primary);line-height:1.5;margin-bottom:4px;">${escapeHTML(q)}</li>`).join('') +
      '</ul>';

    const groups: string[] = [];
    let total = 0;
    const charges = buildChargeInfos(db);

    // (a) SPECIFIC to this juror: cross this juror's actual answers with the
    // charged crime — each question names both. Highest value; listed first.
    const specific = specificQuestions(j, charges);
    if (specific.length) {
      const unique = dedupe(specific.map((s) => s.question)).slice(0, 10);
      total += unique.length;
      const items = specific
        .filter((s) => unique.includes(s.question))
        .map(
          (s) =>
            `<li style="font-size:10.5px;color:var(--text-primary);line-height:1.5;margin-bottom:5px;">${escapeHTML(s.question)}` +
            `<div style="font-size:9px;color:var(--text-muted);margin-top:1px;">↳ ${escapeHTML(s.basis)}</div></li>`
        )
        .join('');
      groups.push(
        `<div style="margin-bottom:8px;"><div style="font-size:10px;font-weight:800;color:#9d174d;">🎯 Specific to this juror × the charged crime</div><ul style="margin:4px 0 0;padding-left:16px;">${items}</ul></div>`
      );
    }

    // (b) Charge-driven: research mapped to the charged statutes.
    const byLabel = new Map<string, string[]>();
    for (const ch of charges) {
      for (const hit of ch.hits) {
        const qs = questionsFromResearch(hit.data);
        if (!qs.length) continue;
        const arr = byLabel.get(hit.label) || [];
        arr.push(...qs);
        byLabel.set(hit.label, arr);
      }
    }
    for (const [label, qs] of byLabel) {
      const unique = dedupe(qs).slice(0, 8);
      if (!unique.length) continue;
      total += unique.length;
      groups.push(
        `<div style="margin-bottom:8px;"><div style="font-size:10px;font-weight:800;color:#6b21a8;">⚖ Charged crime — ${escapeHTML(label)}</div>${qList(unique)}</div>`
      );
    }

    // (c) Juror + spouse background: every answered mapped field's follow-ups,
    // with bracketed templates instantiated against this juror's actual answer.
    const fieldIds: Array<{ id: string; label: string }> = [
      ...GUIDANCE_FIELDS.map((g) => ({ id: g.id, label: g.label })),
      ...SPOUSE_FIELDS
    ];
    for (const f of fieldIds) {
      const v = fieldValueFor(j, f.id);
      if (!v || v === '—') continue;
      const g = db.getFieldGuidance(f.id);
      if (!g || !g.voirDire.length) continue;
      const unique = dedupe(g.voirDire.map((tq) => fillTemplate(tq, v))).slice(0, 3);
      if (!unique.length) continue;
      total += unique.length;
      groups.push(
        `<div style="margin-bottom:8px;"><div style="font-size:10px;font-weight:800;color:#1e40af;">👤 ${escapeHTML(f.label)}: <span style="font-weight:500;">${escapeHTML(v)}</span></div>${qList(unique)}</div>`
      );
    }

    if (!groups.length) return;
    el.innerHTML =
      '<details style="background:#f0f9ff;border:1px solid #bae6fd;border-radius:8px;padding:8px 11px;margin-top:16px;">' +
      `<summary style="font-size:10px;font-weight:700;color:#075985;text-transform:uppercase;letter-spacing:.4px;cursor:pointer;">Suggested voir dire questions (${total}) — charge + juror background</summary>` +
      `<div style="margin-top:8px;max-height:320px;overflow-y:auto;">${groups.join('')}</div>` +
      '<div style="font-size:9.5px;color:var(--text-muted);line-height:1.5;margin-top:4px;">Drawn from the mapped research for the charged crime(s) and this juror’s questionnaire answers. Adapt wording to the court’s voir dire format; strikes may never rest on race, sex, or ethnicity.</div>' +
      '</details>';
  } catch {
    /* research DB unavailable — leave section empty */
  }
}

/**
 * Fill the juror detail's research-guidance section from the offline research
 * DB: for each answered field with mapped guidance, the inference, its
 * strength, and suggested voir dire follow-ups. Async; silent when the DB is
 * unavailable.
 */
async function renderJurorGuidance(j: Juror): Promise<void> {
  const el = byId('jr-guidance');
  if (!el) return;
  try {
    const { getCaselawDb } = await import('@/data/caselaw-db-instance');
    const db = await getCaselawDb();
    if (!db) return;
    const strengthColor: Record<string, string> = { strong: '#166534', moderate: '#9a6a1e', weak: '#b45309' };
    const blocks: string[] = [];
    for (const gf of GUIDANCE_FIELDS) {
      const v = (gf.value(j) || '').trim();
      if (!v || v === '—') continue;
      const g = db.getFieldGuidance(gf.id);
      if (!g || !g.inference) continue;
      const color = strengthColor[g.strength || ''] || '#64748b';
      blocks.push(
        '<div style="background:#fff;border:1px solid #e9d8fd;border-radius:7px;padding:7px 9px;margin-bottom:5px;">' +
          `<div style="font-size:11px;font-weight:700;color:var(--text-primary);">${escapeHTML(gf.label)}: <span style="font-weight:500;">${escapeHTML(v)}</span>` +
          (g.strength
            ? ` <span style="display:inline-block;background:${color};color:#fff;border-radius:4px;padding:0 5px;font-size:8px;font-weight:800;vertical-align:middle;">${escapeHTML(g.strength.toUpperCase())} SIGNAL</span>`
            : '') +
          '</div>' +
          `<div style="font-size:10.5px;color:var(--text-secondary);line-height:1.5;margin-top:3px;">${escapeHTML(g.inference)}</div>` +
          (g.voirDire.length
            ? `<div style="font-size:10px;color:#6b21a8;margin-top:4px;"><b>Ask:</b> ${escapeHTML(g.voirDire[0])}</div>`
            : '') +
          '</div>'
      );
    }
    if (!blocks.length) return;
    el.innerHTML =
      '<details style="background:#faf5ff;border:1px solid #e9d8fd;border-radius:8px;padding:8px 11px;margin-top:16px;">' +
      `<summary style="font-size:10px;font-weight:700;color:#6b21a8;text-transform:uppercase;letter-spacing:.4px;cursor:pointer;">Research-backed guidance (${blocks.length} fields) — probabilistic, verify in voir dire</summary>` +
      `<div style="margin-top:8px;">${blocks.join('')}</div>` +
      '<div style="font-size:9.5px;color:var(--text-muted);line-height:1.5;margin-top:4px;">Inferences are weak-to-moderate signals from published research, not predictions. Strikes may never rest on race, sex, or ethnicity.</div>' +
      '</details>';
  } catch {
    /* research DB unavailable — leave section empty */
  }
}

export function openJurorDetail(id: number): void {
  const j = store.getState().jurors.find((x) => x.id === id);
  if (!j) return;
  const ctx = panelContext();
  const sl = getScoreLabel(scoreJuror(j, ctx), store.getState().panelLens);

  const rows: [string, string][] = [
    ['Gender', j.gender || '—'],
    ['Age Range', j.age || '—'],
    ['Education', j.edu || '—'],
    ['Employment', j.empStatus || '—'],
    ['Business Type', j.bizType || '—'],
    ['Occupation', j.occ || '—'],
    ['Household Size', j.hhSize || '—'],
    ['Parent/Guardian', j.parent || '—'],
    ['Prior Jury', j.jury || '—'],
    ['LE Connection', j.le || '—'],
    ['Law Employment', j.lawEmp || '—'],
    ['Crime Victim', j.victim || '—'],
    ['Accused/Family', j.accused || '—'],
    ['States Fair', j.fair || '—'],
    ['Case Knowledge', j.caseKnow || '—'],
    ['Opinion Stated', j.opinion || '—'],
    ['Spouse Age', j.spouseAge || '—'],
    ['Spouse Education', j.spouseEdu || '—']
  ];

  showModal(
    '<div style="display:flex;align-items:flex-start;gap:10px;margin-bottom:14px;">' +
      `<div class="anon-id" style="width:48px;height:48px;font-size:13px;">${escapeHTML(j.panelId)}</div>` +
      `<div><div class="modal-title" style="margin-bottom:2px;">Seat ${j.seat} · ${escapeHTML(j.gender || '—')}, ${escapeHTML(j.age || '—')}</div>` +
      `<div style="font-size:11px;color:var(--text-muted);">${escapeHTML(j.panelId)} · Score: ${sl.label}</div></div>` +
      '</div>' +
      // Side-aware, charge-aware suitability analysis — async below.
      '<div id="jr-suitability"></div>' +
      // SpatchVenire characteristic assessment (read-only reference; Phase 2).
      svAssessmentHtml(j, ctx) +
      // Voir-dire answer capture — the strike basis (Phase 4b).
      vdCaptureHtml(j, ctx) +
      '<div style="display:grid;grid-template-columns:1fr 1fr;gap:6px;">' +
      rows
        .map((r) => `<div><div class="dl">${r[0]}</div><div class="dv">${escapeHTML(r[1])}</div></div>`)
        .join('') +
      '</div>' +
      // Full questionnaire capture (only present for scanned jurors).
      questionnaireHtml(j) +
      // What was deliberately NOT captured + free-text redaction summary.
      restrictedDataHtml(j) +
      // Research-backed field guidance — filled in asynchronously below.
      '<div id="jr-guidance"></div>' +
      // Suggested voir dire (charge + juror/spouse background) — async below.
      '<div id="jr-voirdire"></div>' +
      // Background re-OCR of illegible fields — filled in asynchronously below.
      '<div id="jr-field-review"></div>' +
      // End-of-selection cleanup: delete this one juror's stored information.
      '<div style="margin-top:16px;padding-top:12px;border-top:1px solid var(--border,#e2e8f0);display:flex;justify-content:flex-end;">' +
      `<button class="btn btn-danger btn-sm" onclick="deleteJuror(${j.id})">🗑 Delete Juror Information</button>` +
      '</div>'
  );
  // Populate the background re-OCR section (async) and keep it live-updating.
  openJurorId = id;
  ensureReviewListener();
  void renderFieldReview(id);
  // Populate the research-guidance section from the offline research DB.
  void renderJurorGuidance(j);
  // Populate the suggested voir dire questions (charge + juror background).
  void renderJurorVoirDire(j);
  // Populate the side-aware, charge-aware suitability analysis.
  void renderJurorSuitability(j);
}

// The juror whose detail modal is currently open (for live re-OCR refresh).
let openJurorId: number | null = null;
let reviewListenerAttached = false;

/** Keep the panel + open modal in sync as the background engine works. */
function ensureReviewListener(): void {
  if (reviewListenerAttached) return;
  reviewListenerAttached = true;
  const modalOpenFor = (id: number | undefined): boolean =>
    openJurorId != null && id === openJurorId && !!byId('modal-overlay')?.classList.contains('open');
  // Progress on an unresolved field → refresh just its re-OCR section.
  window.addEventListener('field-review-updated', (e) => {
    const id = (e as CustomEvent<{ jurorId: number }>).detail?.jurorId;
    if (modalOpenFor(id)) void renderFieldReview(openJurorId as number);
  });
  // A field was auto-repaired → repopulate the panel (scores/flags may change)
  // and the open detail so the recovered value shows immediately.
  window.addEventListener('juror-updated', (e) => {
    const id = (e as CustomEvent<{ jurorId: number }>).detail?.jurorId;
    updatePanelBadge();
    renderPanel();
    if (modalOpenFor(id)) openJurorDetail(id as number);
  });
}

// Fields currently showing their optional manual-entry box (attorney tapped
// "Type it in now"). Scoped to this module, keyed `${jurorId}::${fieldId}`, so
// re-renders (e.g. from the background engine) don't collapse an in-progress
// entry the attorney is mid-typing.
const revealedForManualEntry = new Set<string>();

/** Fetch this juror's illegible-field records and render them into the modal. */
async function renderFieldReview(jurorId: number): Promise<void> {
  const fr = await import('@/ocr/field-review');
  const recs = await fr.getForJuror(jurorId);
  const el = byId('jr-field-review');
  if (!el) return;
  if (!recs.length) { el.innerHTML = ''; return; }
  const esc = (s: string): string => escapeHTML(s);
  let html =
    '<div style="margin-top:16px;padding-top:12px;border-top:1px solid var(--border,#e2e8f0);">' +
    '<div style="font-size:11px;font-weight:700;color:var(--text-muted);text-transform:uppercase;letter-spacing:.4px;margin-bottom:8px;">Illegible fields — background re-scan</div>';
  for (const r of recs) {
    const key = `${jurorId}::${r.fieldId}`;
    html += '<div style="margin-bottom:12px;">' +
      `<div class="dl">${esc(r.label)}</div>` +
      `<img src="${r.image}" alt="scanned field" style="display:block;max-width:100%;height:auto;border:1px solid var(--border,#e2e8f0);border-radius:4px;background:#fff;margin:3px 0;" />`;
    if (r.status === 'pending') {
      // The engine auto-fills this field the moment it deciphers it — the row
      // then disappears and the value appears in the questionnaire above.
      html += `<div style="font-size:11px;color:var(--text-muted);">⏳ Re-scanning in the background — pass ${r.attempts} of 3 (TrOCR → TrOCR → Tesseract)…</div>`;
    } else if (revealedForManualEntry.has(key)) {
      // Optional, on-demand typing — only appears after the attorney taps
      // "Type it in now", for the cases where there IS time. The default,
      // at-rest view (below) is buttons only.
      html += '<div style="font-size:11px;color:var(--text-muted);margin-bottom:3px;">All 3 passes failed to read this — enter it manually:</div>' +
        `<input id="jrf-${esc(r.fieldId)}" type="text" placeholder="Type what you read" style="width:100%;box-sizing:border-box;padding:5px 7px;font-size:12px;border:1px solid #cbd5e1;border-radius:5px;margin-bottom:5px;" /> ` +
        `<button class="btn btn-primary btn-sm" onclick="saveFieldManual(${jurorId},'${esc(r.fieldId)}')">Save</button> ` +
        `<button class="btn btn-secondary btn-sm" onclick="cancelFieldManualEntry(${jurorId},'${esc(r.fieldId)}')">Cancel</button>`;
    } else {
      // Default: no typing, no keyboard — a peer of the "Dismiss" button.
      html += '<div style="font-size:11px;color:var(--text-muted);margin-bottom:5px;">Could not auto-read after all 3 passes (TrOCR ×2, Tesseract).</div>' +
        `<button class="btn btn-secondary btn-sm" onclick="dismissFieldReview(${jurorId},'${esc(r.fieldId)}')">Dismiss — review later</button> ` +
        `<button class="btn btn-secondary btn-sm" onclick="revealFieldManualEntry(${jurorId},'${esc(r.fieldId)}')">✎ Type it in now</button>`;
    }
    html += '</div>';
  }
  html += '</div>';
  el.innerHTML = html;
}

/** Drop a field the background scan gave up on from the review list (one tap, no typing). */
export function dismissFieldReview(jurorId: number, fieldId: string): void {
  revealedForManualEntry.delete(`${jurorId}::${fieldId}`);
  void import('@/ocr/field-review').then((fr) => fr.remove(`${jurorId}::${fieldId}`).then(() => renderFieldReview(jurorId)));
  showToast('Dismissed');
}

/** Reveal the optional manual-entry box for one exhausted field (attorney has a moment to type). */
export function revealFieldManualEntry(jurorId: number, fieldId: string): void {
  revealedForManualEntry.add(`${jurorId}::${fieldId}`);
  void renderFieldReview(jurorId);
}

/** Hide the manual-entry box again without saving anything. */
export function cancelFieldManualEntry(jurorId: number, fieldId: string): void {
  revealedForManualEntry.delete(`${jurorId}::${fieldId}`);
  void renderFieldReview(jurorId);
}

/** Save a manually-typed value for a field all 3 background passes gave up on. */
export function saveFieldManual(jurorId: number, fieldId: string): void {
  const input = byId<HTMLInputElement>(`jrf-${fieldId}`);
  const value = input ? input.value.trim() : '';
  if (!value) { showToast('Nothing typed'); return; }
  applyRecoveredField(jurorId, fieldId, value); // fires juror-updated → repopulates the rest of the modal
  revealedForManualEntry.delete(`${jurorId}::${fieldId}`);
  // Explicitly re-render this section once the record is actually gone — the
  // juror-updated re-render above can race ahead of this removal and briefly
  // show the (already-resolved) field again otherwise.
  void import('@/ocr/field-review').then((fr) => fr.remove(`${jurorId}::${fieldId}`).then(() => renderFieldReview(jurorId)));
  showToast('Saved');
}

/**
 * Permanently delete ONE juror's stored questionnaire information from the panel
 * (and thus from the persisted `voirai_state` in localStorage). For use once a
 * juror is no longer needed. Confirms first; closes the detail modal on success.
 */
export function deleteJuror(id: number): void {
  const j = store.getState().jurors.find((x) => x.id === id);
  if (!j) return;
  confirmApp(
    'Delete Juror Information',
    `Permanently delete all stored information for ${j.panelId} (Seat ${j.seat})? This cannot be undone.`,
    'Delete',
    'btn-danger'
  ).then((ok) => {
    if (!ok) return;
    const s = store.getState();
    s.jurors = s.jurors.filter((x) => x.id !== id);
    store.save(); // removes the juror's record from localStorage immediately
    // Also purge this juror's cached OCR text, if any, so no local data lingers.
    if (j.ocrHash) {
      void import('@/ocr/ocr-cache').then((m) => m.ocrCacheDelete(j.ocrHash as string)).catch(() => {
        /* cache unavailable — nothing to purge */
      });
    }
    // And drop any stored illegible-field crops queued for background re-OCR.
    void import('@/ocr/field-review').then((m) => m.removeForJuror(id)).catch(() => undefined);
    updatePanelBadge();
    renderPanel();
    closeModal();
    showToast(`Deleted ${j.panelId}`);
  });
}

/** Load an array of juror records into the panel (dedup by panelId). */
export function loadJurorArray(arr: Juror[]): void {
  const state = store.getState();
  arr.forEach((d) => {
    if (d.panelId && state.jurors.find((j) => j.panelId === d.panelId)) return;
    state.jurors.push({
      id: state.nextId++,
      panelId: d.panelId || `J-${String(state.nextId).padStart(2, '0')}`,
      seat: d.seat || state.jurors.length + 1,
      gender: d.gender || '',
      age: d.age || '',
      edu: d.edu || '',
      empStatus: d.empStatus || '',
      bizType: d.bizType || '',
      occ: d.occ || '',
      hhSize: d.hhSize || '',
      parent: d.parent || '',
      jury: d.jury || 'No',
      le: d.le || 'None',
      lawEmp: d.lawEmp || 'None',
      victim: d.victim || '',
      accused: d.accused || 'No',
      fair: d.fair || '',
      caseKnow: d.caseKnow || 'None stated',
      opinion: d.opinion || 'None stated',
      notes: d.notes || '',
      status: 'none',
      tags: [],
      ocrHash: d.ocrHash,
      questionnaire: d.questionnaire,
      capturedAt: d.capturedAt
    });
  });
  store.save();
}

export function loadDemoJurors(): void {
  confirmApp(
    'Load 25 Test Jurors',
    'Load all 25 de-identified Suffolk County juror profiles parsed from CJQ2017 forms? Existing jurors are kept. No PII included.',
    'Load All 25',
    'btn-primary'
  ).then((ok) => {
    if (!ok) return;
    loadJurorArray(DEMO_JURORS);
    updatePanelBadge();
    renderPanel();
    showToast('✓ Loaded demo jurors');
  });
}

export function confirmClearPanel(): void {
  confirmApp(
    'Clear All Jurors',
    'Clear ALL jurors and their stored questionnaire data from this device? ' +
      'The law and research database is not affected. ' +
      'Use this once jury selection is complete. This cannot be undone.',
    'Clear All',
    'btn-danger'
  ).then((ok) => {
    if (!ok) return;
    store.getState().jurors = [];
    store.save();
    // Also purge the on-device OCR text cache + any stored illegible-field crops
    // so no recognized questionnaire data lingers. Lazy imports keep startup light.
    void import('@/ocr/ocr-cache').then((m) => m.ocrCacheClear()).catch(() => {
      /* cache unavailable — nothing to purge */
    });
    void import('@/ocr/field-review').then((m) => m.clearAll()).catch(() => undefined);
    updatePanelBadge();
    renderPanel();
    showToast('All jurors cleared');
  });
}
